import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  FormField,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-console";

import { JSON_FORMS_ROUTES } from "src/jsonForms/constants";
import { useAppSelector } from "src/redux/hooks";

import AtozEscalationQuestion from "./AtozEscalationQuestion";
import { userHasLobAccess } from "./lobAccess";
import {
  escalationAnswerFromLocationState,
  type AtozEscalationAnswer,
  type AtozEscalationLocationState,
} from "./atozEscalation";
import type { AtozPreScreenData } from "./types";

interface AtozPreScreenProps {
  onPreScreenSubmit: (data: AtozPreScreenData) => void;
}

const COUNTRY_OPTIONS: SelectProps.Option[] = [
  { label: "United States", value: "United States" },
  { label: "Canada", value: "Canada" },
  { label: "EU/UK", value: "EU/UK" },
];

/**
 * AtoZ pre-screen rendered by `JsonFormsClaimIntakeApp` for the `atoz` LOB.
 *
 * Presents a single country dropdown. Once a country is selected and the
 * user clicks Continue, the schema `atoz` + selected country is resolved.
 *
 * A user who can also file IPL claims is first asked whether the claim meets
 * an exclusion that takes it out of the AtoZ program. "Yes" sends them to the
 * schema-driven IPL pre-screen instead; only "No" reveals the country dropdown.
 */
function AtozPreScreen({ onPreScreenSubmit }: AtozPreScreenProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const userRoles = useAppSelector((state) => state.auth.user.roles);
  // Asked only of users who can reach the IPL form that "Yes" leads to.
  const asksEscalationQuestion = userHasLobAccess(userRoles ?? [], "ipl");

  const [escalationAnswer, setEscalationAnswer] = useState<AtozEscalationAnswer | null>(() =>
    escalationAnswerFromLocationState(location.state)
  );
  const [selectedCountry, setSelectedCountry] = useState<SelectProps.Option | null>(null);

  const isEscalated = asksEscalationQuestion && escalationAnswer === "corporate-pl";
  const showsCountry = !asksEscalationQuestion || escalationAnswer === "atoz";

  const handleCountryChange = useCallback(({ detail }: { detail: SelectProps.ChangeDetail }) => {
    setSelectedCountry(detail.selectedOption);
  }, []);

  const handleSubmit = useCallback(() => {
    if (isEscalated) {
      const state: AtozEscalationLocationState = { atozEscalationAnswer: "corporate-pl" };
      navigate(JSON_FORMS_ROUTES.IPL_CLAIM_INTAKE_FORM, { state });
      return;
    }
    if (showsCountry && selectedCountry?.value) {
      onPreScreenSubmit({ country: selectedCountry.value });
    }
  }, [isEscalated, showsCountry, selectedCountry, navigate, onPreScreenSubmit]);

  const isSubmitDisabled = useMemo(
    () => !isEscalated && !(showsCountry && selectedCountry?.value),
    [isEscalated, showsCountry, selectedCountry]
  );

  return (
    <Box padding="l" data-testid="atoz-pre-screen">
      <SpaceBetween size="m" direction="vertical">
        <Header variant="h2">AtoZ claim intake</Header>

        {asksEscalationQuestion && (
          <AtozEscalationQuestion
            value={escalationAnswer}
            onChange={setEscalationAnswer}
            testIdPrefix="atoz-pre-screen"
          />
        )}

        {showsCountry && (
          <Container>
            <FormField
              label="Country"
              description="Select the country for this claim."
              data-testid="atoz-pre-screen-country-form-field"
            >
              <Select
                data-testid="atoz-pre-screen-country-select"
                options={COUNTRY_OPTIONS}
                selectedOption={selectedCountry}
                onChange={handleCountryChange}
                placeholder="Select a country"
              />
            </FormField>
          </Container>
        )}

        <Button
          variant="primary"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          data-testid="atoz-pre-screen-submit-button"
        >
          Continue
        </Button>
      </SpaceBetween>
    </Box>
  );
}

export default AtozPreScreen;
